import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import { Badge } from '@/components/ui/badge';
import { Plus, Edit, Trash2, Shield, Search, Target } from 'lucide-react';
import { useUpdateBio } from '@/hooks/useProfile';
import { skillApi } from '@/services/skillKnowledgeApi';
import type { Skill } from '@/types/skillKnowledge';
import type { ProfileSkill, BioUpdateRequest } from '@/types/userProfile';
import { SkillLevel } from '@/types/userProfile';
import { useToast } from '@/hooks/useToast';
import { CopyableId } from '@/components/common';

interface SkillManagerProps {
  profileId: string;
  skills: ProfileSkill[];
}

const SKILL_LEVELS: SkillLevel[] = [
  SkillLevel.FOUNDATIONAL,
  SkillLevel.BASIC,
  SkillLevel.INTERMEDIATE,
  SkillLevel.ADVANCED,
  SkillLevel.EXPERT,
  SkillLevel.MASTERY,
];

const getSkillLevelColor = (level?: SkillLevel) => {
  const colors: Record<SkillLevel, string> = {
    [SkillLevel.FOUNDATIONAL]: 'bg-gray-100 text-gray-800 dark:bg-gray-800 dark:text-gray-200',
    [SkillLevel.BASIC]: 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200',
    [SkillLevel.INTERMEDIATE]: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200',
    [SkillLevel.ADVANCED]: 'bg-orange-100 text-orange-800 dark:bg-orange-900 dark:text-orange-200',
    [SkillLevel.EXPERT]: 'bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-200',
    [SkillLevel.MASTERY]: 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200',
  };
  return level ? colors[level] : 'bg-gray-100 text-gray-800';
};

export const SkillManager: React.FC<SkillManagerProps> = ({ profileId, skills: initialSkills }) => {
  const [skills, setSkills] = useState<ProfileSkill[]>(initialSkills);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingIndex, setEditingIndex] = useState<number | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [masterSkills, setMasterSkills] = useState<Skill[]>([]);
  const [selectedSkillId, setSelectedSkillId] = useState('');
  const [skillName, setSkillName] = useState('');
  const [skillLevel, setSkillLevel] = useState<SkillLevel | ''>('');
  const [isVerified, setIsVerified] = useState(false);
  const updateBio = useUpdateBio();
  const { toast } = useToast();

  // Load master skills for selection
  useEffect(() => {
    const loadMasterSkills = async () => {
      try {
        const response = await skillApi.list({ page: 0, size: 100 });
        setMasterSkills(response.content || []);
      } catch (error) {
        console.error('Error loading master skills:', error);
      }
    };
    loadMasterSkills();
  }, []);

  const filteredMasterSkills = masterSkills.filter((skill) =>
    skill.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    skill.id?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const getSkillDisplayName = (skill: ProfileSkill) =>
    skill.name ||
    (masterSkills.find((s) => s.id === skill.skillId) as { name?: string } | undefined)?.name ||
    skill.skillId;

  const handleSave = async () => {
    if (!selectedSkillId) {
      toast({
        variant: 'destructive',
        title: 'Error',
        description: 'Please select a skill',
      });
      return;
    }

    const newSkill: ProfileSkill = {
      skillId: selectedSkillId,
      name: skillName || masterSkills.find((s) => s.id === selectedSkillId)?.name,
      level: skillLevel || undefined,
      verified: isVerified,
    };

    let updatedSkills: ProfileSkill[];
    if (editingIndex !== null) {
      updatedSkills = [...skills];
      updatedSkills[editingIndex] = newSkill;
    } else {
      updatedSkills = [...skills, newSkill];
    }

    setSkills(updatedSkills);

    // Update bio
    const bioUpdate: BioUpdateRequest = {
      skills: updatedSkills,
    };

    try {
      await updateBio.mutateAsync({ userId: profileId, bio: bioUpdate });
      setIsDialogOpen(false);
      resetForm();
    } catch (_error) {
      // Error handled by mutation
    }
  };

  const handleEdit = (index: number) => {
    const skill = skills[index];
    setEditingIndex(index);
    setSelectedSkillId(skill.skillId);
    setSkillName(skill.name || '');
    setSkillLevel(skill.level || '');
    setIsVerified(skill.verified || false);
    setIsDialogOpen(true);
  };

  const handleDelete = async (index: number) => {
    const updatedSkills = skills.filter((_, i) => i !== index);
    setSkills(updatedSkills);

    const bioUpdate: BioUpdateRequest = {
      skills: updatedSkills,
    };

    try {
      await updateBio.mutateAsync({ userId: profileId, bio: bioUpdate });
    } catch (_error) {
      // Error handled by mutation
    }
  };

  const resetForm = () => {
    setEditingIndex(null);
    setSelectedSkillId('');
    setSkillName('');
    setSkillLevel('');
    setIsVerified(false);
    setSearchQuery('');
  };

  const handleOpenChange = (open: boolean) => {
    setIsDialogOpen(open);
    if (!open) resetForm();
  };

  return (
    <Card>
      <CardHeader>
        <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <CardTitle className="flex items-center gap-2">
              <Target className="h-5 w-5 text-violet-600 dark:text-violet-400" />
              Skills
            </CardTitle>
            <CardDescription>Manage user skills and proficiency levels from master data</CardDescription>
          </div>
          <Dialog open={isDialogOpen} onOpenChange={handleOpenChange}>
            <DialogTrigger asChild>
              <Button onClick={() => resetForm()} className="shrink-0">
                <Plus className="mr-2 h-4 w-4" />
                Add skill
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-2xl">
              <DialogHeader>
                <DialogTitle>{editingIndex !== null ? 'Edit Skill' : 'Add Skill'}</DialogTitle>
                <DialogDescription>
                  Select a skill from master data and set user-specific details
                </DialogDescription>
              </DialogHeader>
              <div className="space-y-4 py-4">
                <div>
                  <Label>Search Master Skills</Label>
                  <div className="relative mt-1">
                    <Search className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
                    <Input
                      placeholder="Search skills..."
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      className="pl-9"
                    />
                  </div>
                </div>

                <div>
                  <Label>Select Skill</Label>
                  <Select value={selectedSkillId} onValueChange={setSelectedSkillId}>
                    <SelectTrigger className="mt-1">
                      <SelectValue placeholder="Select a skill" />
                    </SelectTrigger>
                    <SelectContent>
                      {filteredMasterSkills.map((skill) => (
                        <SelectItem key={skill.id ?? ''} value={skill.id ?? ''}>
                          {skill.name} {skill.category && `(${skill.category})`}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                {selectedSkillId && (
                  <>
                    <div>
                      <Label>Skill Name (Optional override)</Label>
                      <Input
                        value={skillName}
                        onChange={(e) => setSkillName(e.target.value)}
                        placeholder="Leave empty to use master skill name"
                        className="mt-1"
                        maxLength={100}
                      />
                    </div>

                    <div>
                      <Label>Proficiency Level</Label>
                      <Select value={skillLevel} onValueChange={(v) => setSkillLevel(v as SkillLevel)}>
                        <SelectTrigger className="mt-1">
                          <SelectValue placeholder="Select level" />
                        </SelectTrigger>
                        <SelectContent>
                          {SKILL_LEVELS.map((level) => (
                            <SelectItem key={level} value={level}>
                              {level}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>

                    <div className="flex items-center space-x-2">
                      <input
                        type="checkbox"
                        id="verified"
                        checked={isVerified}
                        onChange={(e) => setIsVerified(e.target.checked)}
                        className="rounded"
                      />
                      <Label htmlFor="verified" className="cursor-pointer">
                        Verified
                      </Label>
                    </div>
                  </>
                )}
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => handleOpenChange(false)}>
                  Cancel
                </Button>
                <Button onClick={handleSave} disabled={updateBio.isPending}>
                  {updateBio.isPending ? 'Saving...' : 'Save'}
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </div>
      </CardHeader>
      <CardContent>
        {skills.length === 0 ? (
          <div className="rounded-lg border border-dashed bg-muted/20 flex flex-col items-center justify-center py-12 text-center">
            <Target className="h-10 w-10 text-muted-foreground/50 mb-3" />
            <p className="font-medium text-muted-foreground">No skills yet</p>
            <p className="text-sm text-muted-foreground mt-1">Add skills from master data to get started</p>
            <Button onClick={() => handleOpenChange(true)} className="mt-4" variant="outline" size="sm">
              <Plus className="mr-2 h-4 w-4" />
              Add skill
            </Button>
          </div>
        ) : (
          <div className="rounded-lg border bg-muted/20 overflow-hidden">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Name</TableHead>
                <TableHead>Skill ID</TableHead>
                <TableHead>Level</TableHead>
                <TableHead>Verified</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {skills.map((skill, index) => (
                <TableRow key={`${skill.skillId}-${index}`}>
                  <TableCell className="font-medium">{getSkillDisplayName(skill)}</TableCell>
                  <TableCell>
                    <CopyableId id={skill.skillId} label="" showLabel={false} codeClassName="text-xs" />
                  </TableCell>
                  <TableCell>
                    {skill.level ? (
                      <Badge className={getSkillLevelColor(skill.level)}>
                        {skill.level}
                      </Badge>
                    ) : (
                      'N/A'
                    )}
                  </TableCell>
                  <TableCell>
                    {skill.verified ? (
                      <Badge variant="outline" className="bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200">
                        <Shield className="h-3 w-3 mr-1" />
                        Verified
                      </Badge>
                    ) : (
                      <span className="text-muted-foreground">Not verified</span>
                    )}
                  </TableCell>
                  <TableCell className="text-right">
                    <div className="flex justify-end gap-2">
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => handleEdit(index)}
                      >
                        <Edit className="h-4 w-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => {
                          if (window.confirm('Are you sure you want to remove this skill?')) {
                            handleDelete(index);
                          }
                        }}
                      >
                        <Trash2 className="h-4 w-4 text-destructive" />
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
          </div>
        )}
      </CardContent>
    </Card>
  );
};







